import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { emptyFeedbackObj } from '@app/account/profile/user-profile.variables';
import { DictionaryService } from '@app/core/services/bff/dictionary.service';
import { UserProfileBffService } from '@app/core/services/bff/user-profile.service';
import { ReviewsBlockType } from '@app/teachers/teacher-card/teacher-card-variables';
import { LoadingBarService } from '@core/components/spinners/loading-bar/loading-bar.service';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { EContentPanelRoute, ReviewListItem } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { combineLatest, of } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import {
  AuthService,
  CommentService,
  ContextService,
  FeaturesService,
  ReviewsSortOrderType,
  MetaService,
  ProfileService,
  LanguageService,
} from '../../../core/services/';
import { BaseUserProfileSubpageComponent } from '../base-user-profile-subpage.component';

@Component({
  selector: 'app-user-profile-feedback-subpage',
  templateUrl: './user-profile-feedback-subpage.component.html',
  styleUrls: ['./user-profile-feedback-subpage.component.scss'],
})
export class UserProfileFeedbackSubpageComponent
  extends BaseUserProfileSubpageComponent
  implements OnInit, AfterViewInit
{
  @Output() public loaded = new EventEmitter<boolean>();

  public meetingReviews: ReviewsBlockType = {
    items: [],
    loading: false,
    loaded: false,
    filter: {},
    pagination: {
      page: 1,
      pageSize: 10,
      total: 0,
      totalPages: 1,
    },
    order: [['updatedAt', 'DESC']],
  };
  public routes = EContentPanelRoute;
  public order: ReviewsSortOrderType = 'updatedAt';
  public emptyFeedbackObj = emptyFeedbackObj;

  constructor(
    public errorNotificationService: ErrorNotificationService,
    protected router: Router,
    protected authService: AuthService,
    protected contextService: ContextService,
    protected profileService: ProfileService,
    protected userProfileBff: UserProfileBffService,
    protected dictionaryService: DictionaryService,
    protected languageService: LanguageService,
    private commentService: CommentService,
    protected features: FeaturesService,
    protected meta: MetaService,
    protected cdr: ChangeDetectorRef,
    public deviceService: DeviceDetectorService,
    protected loadingBar: LoadingBarService,
  ) {
    super(
      router,
      authService,
      contextService,
      profileService,
      dictionaryService,
      languageService,
      userProfileBff,
      features,
      meta,
      loadingBar,
      cdr,
    );
  }

  public ngOnInit(): void {
    this.loading = true;
    this.loadingBar.fetchingStart(15);

    combineLatest([this.getMe$, this.getUser$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.loading = false;
          this.loadingBar.fetchingComplete();
          this.isMe = this._me && this._user && this._me?.id === this._user.id;
          this.loadMeetingReviews();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.loading = false;
          this.loadingBar.fetchingStop();
        },
      });
  }

  public ngAfterViewInit() {
    if (this.router.url.split('/').pop() === 'feedback') {
      document.querySelector('.speak-wrap').scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  protected loadMeetingReviews() {
    if (!this._user || this.meetingReviews.loading) {
      return;
    }

    this.meetingReviews.loading = true;

    this.commentService
      .findReviews('teacher', this._user.id, this.meetingReviews.pagination, this.meetingReviews.order)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this.meetingReviews.items = [...this.meetingReviews.items, ...response.results];
          this.meetingReviews.pagination = response.pagination;
          this.meetingReviews.loading = false;
          this.meetingReviews.loaded = true;
          this.loaded.emit(true);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.meetingReviews.loading = false;
          this.loaded.emit(true);
        },
      });
  }

  public setReviewsSortOrder(orderBy: keyof ReviewListItem) {
    if (orderBy !== this.order) {
      this.order = orderBy;
      this.meetingReviews.items = [];
      this.meetingReviews.order = [[orderBy, 'DESC']];
      this.meetingReviews.pagination.page = 1;
      this.loadMeetingReviews();
    }
  }

  public onInViewportBottomChange(reached) {
    if (reached.visible && this.meetingReviews.pagination.page < this.meetingReviews.pagination.totalPages) {
      this.meetingReviews.pagination.page += 1;
      this.loadMeetingReviews();
    }
  }

  public trackReviewByFn(index, item: ReviewListItem): string {
    return item.entryId;
  }
}
